import React from 'react'
import clsx from 'clsx'
import { Clock, Layers } from 'lucide-react'
import type { DashboardStage } from '@/hooks/useDashboardCrm'
import { formatIdleDays, formatNumber, formatPercent } from '@/lib/dashboardFormat'
import { useCurrency } from '@/hooks/useCurrency'
import { useTranslation } from 'react-i18next'

const stageColors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
]

/** Above this, a stage is stalled rather than simply in progress */
const IDLE_WARNING_DAYS = 7

interface FunnelByStageProps {
  stages: DashboardStage[]
  loading?: boolean
  /** Drops the card chrome when the block already sits inside a container */
  bare?: boolean
}

const FunnelByStage: React.FC<FunnelByStageProps> = ({ stages, loading = false, bare = false }) => {
  const { t } = useTranslation('reports')
  const { formatCurrency } = useCurrency()
  const maxValue = Math.max(...stages.map((stage) => stage.value), 0)
  const maxCount = Math.max(...stages.map((stage) => stage.count), 0)
  const hasAnyValue = maxValue > 0

  const shellClass = clsx(
    'flex flex-col gap-5',
    !bare && 'rounded-[20px] border border-border bg-card p-6 shadow-sm',
  )

  if (loading) {
    return (
      <div className={shellClass}>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="mb-2 h-4 w-1/3 rounded bg-card-muted" />
            <div className="h-3 w-full rounded-full bg-card-muted" />
          </div>
        ))}
      </div>
    )
  }

  if (stages.length === 0) {
    return (
      <div
        className={clsx(
          'flex flex-col items-center justify-center gap-2 text-center',
          bare
            ? 'py-6'
            : 'rounded-[20px] border border-border bg-card p-10 shadow-sm',
        )}
      >
        <Layers className="h-6 w-6 text-muted-foreground" />
        <p className="text-[14px] font-medium text-foreground">{t('dashboard.stagesEmpty')}</p>
        <p className="max-w-[320px] text-[12px] text-muted-foreground">
          {t('dashboard.stagesEmptyDesc')}
        </p>
      </div>
    )
  }

  return (
    <div className={shellClass}>
      {stages.map((stage, index) => {
        const color = stageColors[index % stageColors.length]
        // Bars follow value when there is any, because a stage holding little volume and
        // a lot of money is the one that deserves attention first
        const reference = hasAnyValue ? maxValue : maxCount
        const current = hasAnyValue ? stage.value : stage.count
        const width = reference > 0 ? (current / reference) * 100 : 0
        const idle = formatIdleDays(stage.avg_idle_days)
        const isStalled = (stage.avg_idle_days ?? 0) >= IDLE_WARNING_DAYS

        return (
          <div key={stage.stage_id} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="text-[14px] font-medium text-foreground">{stage.name}</span>
                <span className="text-[12px] text-muted-foreground">
                  {t('dashboard.leadsCount', { count: stage.count })}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {idle && stage.count > 0 && (
                  <span
                    className={clsx(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                      isStalled
                        ? 'bg-amber-50 text-[#B45309] dark:bg-amber-500/15 dark:text-amber-200'
                        : 'bg-card-muted text-muted-foreground',
                    )}
                    title={t('dashboard.idleTitle')}
                  >
                    <Clock className="h-3 w-3" strokeWidth={2.5} />
                    {idle}
                  </span>
                )}
                <span className="text-[14px] font-semibold tabular-nums text-foreground">
                  {formatCurrency(stage.value)}
                </span>
                <span className="w-12 text-right text-[11px] text-muted-foreground">
                  {formatPercent(stage.pct_of_value)}
                </span>
              </div>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-card-muted">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.max(width, current > 0 ? 4 : 0)}%`,
                  backgroundColor: color,
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default FunnelByStage
